import React, { useState, useEffect, useMemo } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Stack,
  FormControlLabel,
  Checkbox,
  Typography,
  Alert,
  CircularProgress,
  FormGroup,
  Box,
} from '@mui/material'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../redux/store'
import { PatientService } from '../../../../services/patient.service'
import {
  RPMEnrollment,
  ConsentMethod,
  CONSENT_METHOD_DISPLAY,
} from '../../../../model/rpm.types'

interface IProps {
  open: boolean
  onClose: () => void
  onSuccess: (newEnrollments: RPMEnrollment[]) => void
  patientId?: number
}

const patientService = new PatientService()

// RPM-eligible programs
const RPM_ELIGIBLE_PROGRAMS = [
  'hypertension',
  'heart_failure_with_reduced_ejection_fraction',
  'heart_failure_with_preserved_ejection_fraction',
  'volume_overload_in_heart_failure',
  'pulmonary_congestion',
]

// Display names for programs
const PROGRAM_DISPLAY_NAMES: Record<string, string> = {
  hypertension: 'Hypertension',
  heart_failure_with_reduced_ejection_fraction: 'HFrEF',
  heart_failure_with_preserved_ejection_fraction: 'HFpEF',
  volume_overload_in_heart_failure: 'Volume Overload',
  pulmonary_congestion: 'Pulmonary Congestion',
}

const RPMEnrollmentDialog: React.FC<IProps> = ({ open, onClose, onSuccess, patientId }) => {
  const patientDetail = useSelector((state: RootState) => state.patientDetail.patientDetail)
  const [submitting, setSubmitting] = useState(false)

  // Form state
  const [selectedPrograms, setSelectedPrograms] = useState<string[]>([])
  const [consentObtained, setConsentObtained] = useState(false)
  const [consentMethod, setConsentMethod] = useState<ConsentMethod | ''>('')
  const [consentNotes, setConsentNotes] = useState('')

  // Get eligible programs for this patient
  const eligiblePrograms: string[] = useMemo(() => {
    if (!patientDetail?.program_enrollments) return []
    return patientDetail.program_enrollments
      .filter((e: any) => {
        const programName = (e.program || e.program_name || '').toLowerCase()
        return RPM_ELIGIBLE_PROGRAMS.includes(programName)
      })
      .map((e: any) => (e.program || e.program_name || '').toLowerCase())
  }, [patientDetail?.program_enrollments])

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      // Pre-select all eligible programs by default
      setSelectedPrograms([...eligiblePrograms])
    } else {
      setSelectedPrograms([])
      setConsentObtained(false)
      setConsentMethod('')
      setConsentNotes('')
    }
  }, [open, eligiblePrograms])

  const handleProgramToggle = (program: string) => {
    setSelectedPrograms((prev) =>
      prev.includes(program) ? prev.filter((p) => p !== program) : [...prev, program]
    )
  }

  const handleSubmit = async () => {
    if (!patientId || selectedPrograms.length === 0) return

    setSubmitting(true)
    try {
      const payload: any = {
        consent_status: consentObtained ? 'obtained' : 'pending',
        programs: selectedPrograms,
      }

      if (consentObtained && consentMethod) {
        payload.consent_method = consentMethod
      }
      if (consentNotes) {
        payload.consent_notes = consentNotes
      }

      const res = await patientService.createRPMEnrollment(patientId, payload)
      if (res?.data?.success) {
        onSuccess(res.data.rpm_enrollments || [])
      } else {
        console.error('Failed to create RPM enrollment:', res?.data?.error)
      }
    } catch (err) {
      console.error('Failed to create RPM enrollment:', err)
    } finally {
      setSubmitting(false)
    }
  }

  const patientName = patientDetail?.first_name
    ? `${patientDetail.first_name} ${patientDetail.last_name || ''}`
    : 'this patient'

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>Enable RPM Billing</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Enable Remote Patient Monitoring billing for {patientName}.
          </Typography>

          {/* Program Selection */}
          {eligiblePrograms.length > 0 ? (
            <Box>
              <Typography variant="body2" fontWeight="medium" mb={0.5}>
                Programs
              </Typography>
              <FormGroup>
                {eligiblePrograms.map((program) => (
                  <FormControlLabel
                    key={program}
                    control={
                      <Checkbox
                        size="small"
                        checked={selectedPrograms.includes(program)}
                        onChange={() => handleProgramToggle(program)}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        {PROGRAM_DISPLAY_NAMES[program] || program}
                      </Typography>
                    }
                  />
                ))}
              </FormGroup>
            </Box>
          ) : (
            <Alert severity="info" sx={{ py: 0.5 }}>
              No RPM-eligible programs found.
            </Alert>
          )}

          {/* Consent Section */}
          <Box>
            <Typography variant="body2" fontWeight="medium" mb={0.5}>
              Consent
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={consentObtained}
                  onChange={(e) => setConsentObtained(e.target.checked)}
                />
              }
              label={<Typography variant="body2">Patient consent obtained</Typography>}
            />
          </Box>

          {consentObtained && (
            <Stack spacing={1.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Consent Method</InputLabel>
                <Select
                  value={consentMethod}
                  onChange={(e) => setConsentMethod(e.target.value as ConsentMethod)}
                  label="Consent Method"
                >
                  {Object.entries(CONSENT_METHOD_DISPLAY).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Consent Notes (Optional)"
                size="small"
                multiline
                rows={2}
                value={consentNotes}
                onChange={(e) => setConsentNotes(e.target.value)}
                placeholder="Any additional notes..."
              />
            </Stack>
          )}

          {!consentObtained && (
            <Alert severity="warning" sx={{ py: 0.5 }}>
              Consent can be recorded later, but is required before billing.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button size="small" onClick={onClose}>Cancel</Button>
        <Button
          size="small"
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || selectedPrograms.length === 0}
        >
          {submitting ? <CircularProgress size={16} /> : 'Enable RPM'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default RPMEnrollmentDialog
